import React, { useState } from "react";
import CompanionChatLayout from '@/components/chat/CompanionChatLayout';
import { MessageBubble } from './MessageBubble';
import AutoTextarea from '@/components/ui/auto-textarea';
import { Button } from "@/components/ui/button";
import { Send, Volume2, Square } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PrayerContext } from "@/types/prayerFlow";
import { ChatMessage } from '@/types/chat';
import ConversationalThemeSelection from "./ConversationalThemeSelection";
import AbielGuidanceDisplay from './AbielGuidanceDisplay';
import useGeneratedPrayerAudio from '@/hooks/useGeneratedPrayerAudio';
import { DEFAULT_VOICE } from '@/constants/voiceConstants';
import { AbielGuidance } from '@/utils/guidance/guidanceParser';
import { useSettings } from '@/contexts/settings';

// Component for TTS controls in prayer guidance
const GuidanceAudioControls: React.FC<{ guidance: AbielGuidance }> = ({ guidance }) => {
  // Create narration text from guidance data
  const createNarrationText = (guidance: AbielGuidance): string => {
    let narration = `Prayer Guidance. ${guidance.summary} `;
    
    if (guidance.reading_goals?.length) {
      narration += `Reading Goals: ${guidance.reading_goals.join('. ')} `;
    }
    
    if (guidance.how_to_pray?.outline) {
      const outline = guidance.how_to_pray.outline;
      narration += `Prayer Structure: `;
      if (outline.address) narration += `Address: ${outline.address}. `;
      if (outline.praise) narration += `Praise: ${outline.praise}. `;
      if (outline.confession) narration += `Confession: ${outline.confession}. `;
      if (outline.request) narration += `Request: ${outline.request}. `;
      if (outline.submission) narration += `Submission: ${outline.submission}. `;
      if (outline.thanksgiving) narration += `Thanksgiving: ${outline.thanksgiving}. `;
    }
    
    if (guidance.how_to_pray?.bullet_points?.length > 0) {
      narration += `Prayer Points: ${guidance.how_to_pray.bullet_points.join('. ')} `;
    }
    
    if (guidance.prompts?.en?.length > 0) {
      narration += `Reflection Prompts: ${guidance.prompts.en.join('. ')} `;
    }
    
    return narration.trim();
  };

  const guidanceText = createNarrationText(guidance);
  const guidanceId = `guidance-${Date.now()}`;
  
  const { 
    playNow,
    stop,
    isLoading,
    didAutoplay,
    url,
    error: ttsError 
  } = useGeneratedPrayerAudio(
    { id: guidanceId, content: guidanceText },
    {
      auto: false,
      speak: { voice: DEFAULT_VOICE, model: 'gpt-4o-mini-tts', format: 'mp3' }
    }
  );

  const handlePlay = async () => {
    try {
      await playNow();
    } catch (error) {
      console.error('TTS playback failed:', error);
    }
  };

  const handleStop = () => {
    stop();
  };

  const isPlaying = didAutoplay || (url && !isLoading);

  return (
    <div className="flex justify-end mt-2">
      {!isPlaying ? (
        <Button
          onClick={handlePlay}
          variant="outline"
          size="sm"
          disabled={isLoading || !guidanceText}
          className="flex items-center gap-1"
        >
          <Volume2 className="h-4 w-4" />
          <span className="text-xs">Listen</span>
        </Button>
      ) : (
        <Button
          onClick={handleStop}
          variant="outline"
          size="sm"
          className="flex items-center gap-1"
        >
          <Square className="h-4 w-4" />
          <span className="text-xs">Stop</span>
        </Button>
      )}
    </div>
  );
};

// Use the new API helper for consistency with shared parsing utilities
async function generatePrayerDirectly(context: PrayerContext): Promise<AbielGuidance> {
  const { abielChat } = await import('./lib/abielApi');
  const { parseGuidanceResponse } = await import('@/utils/guidance/guidanceParser');
  
  // Build prayer request message
  const systemPrompt = 'You are Abiel, a Christian prayer companion. Provide thoughtful, Scripture-centered PRAYER GUIDANCE. Do not generate prayers. Instead, guide the user with passages, outlines, and prompts for reflection. Use the term "prayer guide" or "prayer guidance" and not "prayer". Output should be structured JSON for the UI.';
  const userContent = [
    context.requestText,
    context.themeTags?.length ? `Themes: ${context.themeTags.join(', ')}` : '',
    context.subject ? `For: ${context.subject}` : '',
    context.includeVerse ? 'Please include a relevant Bible verse.' : '',
    context.language === 'bilingual' ? 'Please provide both English and Chinese guidance.' : ''
  ].filter(Boolean).join('\n');

  const messages = [
    { role: 'system' as const, content: systemPrompt },
    { role: 'user' as const, content: userContent }
  ];

  try {
    const result = await abielChat(messages, { topic: 'guide' });
    console.log('[generatePrayerDirectly] API result:', result);
    console.log('[generatePrayerDirectly] Guidance content:', result.guidance);
    
    // Use shared parsing utility for consistency
    return parseGuidanceResponse(result.guidance);

  } catch (error) {
    console.error('[generatePrayerDirectly] Error:', error);
    
    // Use shared parsing utility for fallback handling
    return parseGuidanceResponse(null);
  }
}

export default function AbielPrayerChat() {
  const { settings } = useSettings();
  const [messages, setMessages] = useState<ChatMessage[]>([
    { 
      id: "welcome",
      role: "assistant", 
      content: "🙏 How can I provide prayer guidance for you today?",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showInterface, setShowInterface] = useState(true);
  const [prayerOptions, setPrayerOptions] = useState({
    selectedThemes: [] as string[],
    includeVerse: false,
    bilingual: false,
    subject: undefined as string | undefined
  });

  const handleOptionsChange = (options: {
    selectedThemes: string[];
    includeVerse: boolean;
    bilingual: boolean;
    subject?: string;
  }) => {
    setPrayerOptions({
      selectedThemes: options.selectedThemes,
      includeVerse: options.includeVerse,
      bilingual: options.bilingual,
      subject: options.subject ?? undefined
    });
  };

  const generatePrayer = async () => {
    if (!input.trim()) return;

    // Add user message
    const userMsg: ChatMessage = { 
      id: Date.now().toString(),
      role: "user", 
      content: input.trim(),
      timestamp: new Date()
    };
    setMessages(m => [...m, userMsg]);

    // Hide interface and start loading
    setShowInterface(false);
    setLoading(true);

    try {
      // Create prayer context
      const context: PrayerContext = {
        requestText: input.trim(),
        themeTags: prayerOptions.selectedThemes.length > 0 ? prayerOptions.selectedThemes : undefined,
        includeVerse: prayerOptions.includeVerse,
        subject: prayerOptions.subject,
        language: prayerOptions.bilingual ? 'bilingual' : 'en'
      };

      const { abielGuidance } = await import('./lib/abielApi');
      const { parseGuidanceResponse } = await import('@/utils/guidance/guidanceParser');

      const result = await abielGuidance({
        requestText: context.requestText,
        subject: context.subject,
        selectedTags: context.themeTags,
        bilingual: context.language === 'bilingual',
        allowedReferences: undefined,
        provider: settings.guidanceProvider
      });

      const guidanceData = parseGuidanceResponse(result.guidance);

      const assistantMsg: ChatMessage & { guidanceData?: AbielGuidance } = { 
        id: Date.now().toString(),
        role: "assistant", 
        content: guidanceData.summary,
        timestamp: new Date(),
        guidanceData
      };
      setMessages(m => [...m, assistantMsg]);
    } catch (error) {
      console.error('Prayer guidance generation error:', error);
      const errorMsg: ChatMessage = { 
        id: Date.now().toString(),
        role: "assistant", 
        content: "I apologize, I'm having trouble generating your prayer guidance right now. Please try again.",
        timestamp: new Date()
      };
      setMessages(m => [...m, errorMsg]);
    } finally {
      setLoading(false);
      setInput("");
      // Reset to initial state
      setShowInterface(true);
      setPrayerOptions({
        selectedThemes: [],
        includeVerse: false,
        bilingual: false,
        subject: undefined
      });
    }
  };

  // Footer with input field when interface is visible
  const footer = showInterface ? (
    <div className="p-4 bg-background/80 backdrop-blur-sm border-t">
      <div className="flex items-end gap-3">
        <AutoTextarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onEnter={generatePrayer}
          placeholder="Tell me how I can guide your prayer today..."
          disabled={loading}
          className="flex-1"
          maxRows={3}
        />
        <Button
          onClick={generatePrayer}
          disabled={!input.trim() || loading}
          size="sm"
          className="flex-shrink-0"
        >
          <Send className="h-4 w-4" />
          <span className="ml-1 hidden sm:inline">Get Prayer Guidance</span>
        </Button>
      </div>
      {!input.trim() && (
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Please tell me what you'd like prayer guidance about
        </p>
      )}
    </div>
  ) : null;

  return (
    <CompanionChatLayout header={null} footer={footer}>
      {messages.map((message) => {
        const extendedMessage = message as ChatMessage & { guidanceData?: AbielGuidance };
        if (extendedMessage.role === 'assistant' && extendedMessage.guidanceData) {
          return (
            <div key={extendedMessage.id || `${extendedMessage.timestamp}-${extendedMessage.content.slice(0, 10)}`} className="flex justify-start px-4 mb-4">
              <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-4 py-3 max-w-[90%]">
                <AbielGuidanceDisplay 
                  guidance={extendedMessage.guidanceData} 
                  esvReference={null}
                  usedESVVerse={false}
                />
                <GuidanceAudioControls guidance={extendedMessage.guidanceData} />
              </div>
            </div>
          );
        }
        return (
          <MessageBubble key={message.id || `${message.timestamp}-${message.content.slice(0, 10)}`} message={message} />
        );
      })}
      
      {/* Show conversational theme selection interface */}
      {showInterface && (
        <div className="px-4 my-6">
          <ConversationalThemeSelection
            onOptionsChange={handleOptionsChange}
            disabled={loading}
          />
        </div>
      )}
      
      {loading && (
        <div className="flex justify-start px-4">
          <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-4 py-3 max-w-[80%]">
            <div className="flex items-center space-x-1">
              <div className="animate-pulse">Abiel is preparing prayer guidance...</div>
            </div>
          </div>
        </div>
      )}
    </CompanionChatLayout>
  );
}
